
























































































import Vue from 'vue'
import AllMetricsLiveCard from '@/components/all-metrics-live/AllMetricsLiveCard.vue'
import {
  LIVE_DASH_STOP_POLLING_TIMESTAMP,
  LIVE_DASHBOARD_TYPES,
  titleByLiveDashboardTypes,
} from '@/constants/live-dashboard'
import { ONE_MINUTE_TIMESTAMP } from '@/components/patient-stats/graphs/mapping-chart-options/constants'
import { PATIENT_NOUN, INCLUDE_ALL_METRICS_LIVE_AUDIO_ALERT } from '@/constants/app-variables'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import {
  ActionNames as PatientWithLiveDataActionNames,
  GetterNames as PatientWithLiveDataGetterNames,
  MutationNames as PatientWithLiveDataMutationNames,
} from '@/store/modules/patient-with-live-data-latest/keys'
import { getPatientName } from '@/utils/patients'
import { hasConcerningVitalSigns } from '@/utils/live-data'
import { Timeouts } from '@/constants/timeouts'
import { ActionNames as PatientActionNames, GetterNames as PatientGetterNames } from '@/store/modules/patient/keys'
import moment from 'moment-timezone'
import { LivePatientsWithHistories } from '@/store/modules/patient-with-live-data-latest/types'
import { FlatPatientWithLiveDataLatest } from '@/store/modules/patient-with-live-data-latest/getters'

const enum Filter {
  All = 'all',
  Live = 'live',
  Alerts = 'alerts',
}

export default Vue.extend({
  components: {
    AllMetricsLiveCard,
  },
  data() {
    return {
      currentTab: LIVE_DASHBOARD_TYPES.OxygenSaturations,
      selectedFilterIndex: 0,
      patientNoun: PATIENT_NOUN,
      stopPollingTimeout: null,
      pollingTime: 3000,
      isInitialized: false,
      isStoppedPolling: false,
      showResumeModal: false,

      //Japan project - concerning vital signs
      watchForConcerningVitalSigns: INCLUDE_ALL_METRICS_LIVE_AUDIO_ALERT,
      showConcerningVitalSignSnackbar: false,
      concerningVitalSignAudioAlert: null,
      concerningVitalSignAlertAudioInterval: null,

      //for window focus
      hidden: '',
      visibilityChange: '',
    }
  },
  computed: {
    ...mapState(ModuleNames.patientWithLiveDataLatest, ['lastUpdate']),
    ...mapGetters(ModuleNames.patient, [PatientGetterNames.activePatients]),
    ...mapGetters(ModuleNames.patientWithLiveDataLatest, [PatientWithLiveDataGetterNames.patientWithLiveDataLatests]),
    activePatientIds() {
      return this[PatientGetterNames.activePatients].map(patient => patient.id)
    },
    filterOptions() {
      return [
        { label: 'ALL', value: Filter.All },
        { label: `Live ${this.patientNoun}s`, value: Filter.Live },
        //Japan project - enable filter for patients with alerts
        this.watchForConcerningVitalSigns
          ? {
              label: `${this.patientNoun}s With Alerts`,
              value: Filter.Alerts,
              hasAlert: this.patientWithConcerningVitalSignIds.length >= 1,
              icon: 'mdi-alert-circle',
            }
          : null,
      ].filter(option => option)
    },
    selectedFilter() {
      return this.filterOptions[this.selectedFilterIndex]
    },
    filteredPatientWithLiveDataLatests() {
      return [...this[PatientWithLiveDataGetterNames.patientWithLiveDataLatests]]
        .filter(patientWithLiveData => this.activePatientIds.includes(patientWithLiveData.id))
        .sort((a, b) =>
          getPatientName(a)
            .toLowerCase()
            .localeCompare(getPatientName(b).toLowerCase())
        )
    },
    patientWithConcerningVitalSignIds() {
      //return ['pdf6yc5t', 'pyocmdc3']
      if (!this.watchForConcerningVitalSigns) {
        return []
      }
      return this.filteredPatientWithLiveDataLatests
        .filter(patient => {
          return hasConcerningVitalSigns(patient)
        })
        .map(patient => patient.id)
    },
    concerningVitalSignSnackbarText() {
      const numberOfPatients = this.patientWithConcerningVitalSignIds.length
      const isPlural = numberOfPatients > 1
      const patientNoun = isPlural ? `${PATIENT_NOUN}s` : PATIENT_NOUN
      return `Alerts for ${numberOfPatients} ${patientNoun}`
    },
    lastUpdateLabel() {
      return moment(this.lastUpdate).format('ddd, MMM D hh:mm:ss A')
    },
  },
  watch: {
    patientWithConcerningVitalSignIds: {
      immediate: true,
      handler(newArr, oldArr) {
        if (newArr.length === 0) {
          return (this.showConcerningVitalSignSnackbar = false)
        }
        const prevArr = oldArr || []
        const noNewAlerts = newArr.every(id => {
          return prevArr.includes(id) ? true : false
        })
        if (noNewAlerts) {
          return
        }
        this.showConcerningVitalSignSnackbar = true
      },
    },
  },
  created() {
    this.startWindowFocusHandling()
    this.concerningVitalSignAudioAlert = new Audio(require('@/assets/audio-files/live_data_vital_sign_warning.wav'))
    if (this.watchForConcerningVitalSigns) {
      console.info(
        "Watching for concerning vital signs. An alert will sound when vital signs exceed thresholds defined in '@/constants/live-dashboard' - see INCLUDE_ALL_METRICS_LIVE_AUDIO_ALERT under '@/constants/app-variables' for more info"
      )
    }
  },
  async mounted() {
    this[PatientWithLiveDataMutationNames.resetPatientWitLiveData]()
    await this[PatientActionNames.getPatients]()
    await this.fetchData()
    this.resumePolling()
    this.isInitialized = true

    //enables audio alert for Japan project
    if (this.watchForConcerningVitalSigns) {
      this.concerningVitalSignAlertAudioInterval = setInterval(() => {
        if (this.showConcerningVitalSignSnackbar) {
          this.concerningVitalSignAudioAlert.play()
        }
      }, Timeouts.ThreeSeconds)
    }
  },
  beforeDestroy(): void {
    this.stopPolling()
    clearInterval(this.concerningVitalSignAlertAudioInterval)
    document.removeEventListener(this.visibilityChange, this.handleVisibilityChange, false)
  },
  methods: {
    ...mapActions(ModuleNames.patient, [PatientActionNames.getPatients]),
    ...mapMutations(ModuleNames.patientWithLiveDataLatest, [PatientWithLiveDataMutationNames.resetPatientWitLiveData]),
    ...mapActions(ModuleNames.patientWithLiveDataLatest, [
      PatientWithLiveDataActionNames.getPatientWithLiveDataLatests,
    ]),
    resumePolling() {
      console.log(new Date(), 'start polling')
      this.isStoppedPolling = false
      this.hideResumeDialog()
      this.startPolling()
    },
    startPolling() {
      if (this.isStoppedPolling) return
      setTimeout(async () => {
        try {
          await this.fetchData()
        } catch (e) {
          console.log('error', e)
        }
        this.startPolling()
      }, this.pollingTime)
    },
    async fetchData() {
      await this[PatientWithLiveDataActionNames.getPatientWithLiveDataLatests]({
        startTimestamp: new Date().getTime() - ONE_MINUTE_TIMESTAMP,
        endTimestamp: new Date().getTime(),
      })
    },
    stopPolling() {
      this.isStoppedPolling = true
    },
    showResumeDialog() {
      this.showResumeModal = true
    },
    hideResumeDialog() {
      this.showResumeModal = false
    },
    patientHasConcerningVitalSigns(patientWithLiveDataLatest) {
      return this.patientWithConcerningVitalSignIds.includes(patientWithLiveDataLatest.id)
    },
    goToPatientsWithAlerts() {
      this.closeConcerningVitalSignSnackbar()
      this.selectedFilterIndex = 2
    },
    closeConcerningVitalSignSnackbar() {
      this.showConcerningVitalSignSnackbar = false
    },
    startWindowFocusHandling() {
      // https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
      if (typeof document.hidden !== 'undefined') {
        // Opera 12.10 and Firefox 18 and later support
        this.hidden = 'hidden'
        this.visibilityChange = 'visibilitychange'
      } else if (typeof (document as any).msHidden !== 'undefined') {
        this.hidden = 'msHidden'
        this.visibilityChange = 'msvisibilitychange'
      } else if (typeof (document as any).webkitHidden !== 'undefined') {
        this.hidden = 'webkitHidden'
        this.visibilityChange = 'webkitvisibilitychange'
      }

      document.addEventListener(this.visibilityChange, this.handleVisibilityChange, false)
    },
    handleVisibilityChange() {
      if (document[this.hidden]) {
        console.log(new Date(), 'lost focus')
        this.stopPollingTimeout = setTimeout(() => {
          console.log(new Date(), 'stop polling')
          this.stopPolling()
        }, LIVE_DASH_STOP_POLLING_TIMESTAMP)
      } else {
        window.clearTimeout(this.stopPollingTimeout)
        if (this.isStoppedPolling) this.showResumeDialog()
      }
    },
    shouldShowCard(patientWithLiveDataLatest: FlatPatientWithLiveDataLatest) {
      if (this.selectedFilter.value === Filter.All) return true
      if (this.selectedFilter.value === Filter.Alerts) {
        return this.patientWithConcerningVitalSignIds.includes(patientWithLiveDataLatest.id)
      }
      return (
        patientWithLiveDataLatest.oxygenSaturation ||
        patientWithLiveDataLatest.pulseRate ||
        patientWithLiveDataLatest.bodyTemperature ||
        patientWithLiveDataLatest.bodyWeight
      )
    },
  },
})
